import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Sparkles, Phone, Mail, MapPin, Instagram, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { portfolioImages, whyChooseUs } from '../data/mock';

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const goToBooking = () => {
    navigate('/book');
  };

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % portfolioImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % portfolioImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + portfolioImages.length) % portfolioImages.length);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center border-2 border-zinc-700">
                <Sparkles className="w-6 h-6 text-zinc-300" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Mad Max Wax</h1>
                <p className="text-xs text-zinc-400">Mobile Detailing LLC</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={goToBooking}
                data-testid="header-book-now-btn"
                className="bg-white text-black px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-zinc-200 transition-all"
              >
                Book Now
              </button>
              <a href="#why-us" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium">Why Us</a>
              <a href="#contact" className="text-zinc-300 hover:text-white transition-colors text-sm font-medium">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-900 opacity-50"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center space-x-2 mb-6">
            <MapPin className="w-5 h-5 text-zinc-400" />
            <span className="text-zinc-400 text-sm">Central Columbus, OH</span>
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
            Bring Your Vehicle Back
            <br />
            <span className="text-zinc-400">to a Showroom Finish</span>
          </h2>
          <p className="text-xl text-zinc-300 mb-8 max-w-2xl leading-relaxed">
            A fully mobile detailing service. We bring showroom-quality results to any location — while you relax at home or work. Premium service. Zero hassle.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={goToBooking}
              data-testid="hero-book-now-btn"
              className="bg-white text-black px-8 py-4 rounded-md text-base font-semibold hover:bg-zinc-200 transition-all flex items-center space-x-2"
            >
              <Calendar className="w-5 h-5" />
              <span>Book Now</span>
            </button>
            <a
              href="#portfolio"
              data-testid="view-work-btn"
              className="bg-zinc-800 text-white px-8 py-4 rounded-md text-base font-semibold hover:bg-zinc-700 transition-all border border-zinc-700"
            >
              View Our Work
            </a>
          </div>
        </div>
      </section>

      {/* Portfolio Slider Section */}
      <section id="portfolio" className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Our Work</h2>
            <p className="text-zinc-400 text-lg">See the Mad Max Wax difference — every detail handled with care.</p>
          </div>

          {/* Carousel */}
          <div className="relative max-w-5xl mx-auto">
            <div className="relative aspect-[16/10] sm:aspect-[16/9] rounded-2xl overflow-hidden bg-zinc-900">
              {portfolioImages.map((image, index) => (
                <div
                  key={index}
                  data-testid={`portfolio-slide-${index}`}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Mad Max Wax detail work ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
              ))}

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                data-testid="carousel-prev-btn"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-all border border-white/10"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={nextSlide}
                data-testid="carousel-next-btn"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-all border border-white/10"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {/* Indicator Dots */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
                {portfolioImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    data-testid={`carousel-dot-${index}`}
                    className={`h-2 rounded-full transition-all ${
                      index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Slide counter */}
            <div className="text-center mt-6 text-zinc-500 text-sm">
              {currentSlide + 1} / {portfolioImages.length}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Why Choose Mad Max Wax</h2>
            <p className="text-zinc-400 text-lg">We go above and beyond to deliver a showroom finish at your doorstep.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div key={index} data-testid={`why-us-${index}`} className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center flex-shrink-0 border border-zinc-800">
                  <item.icon className="w-8 h-8 text-zinc-300" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Ready for That Showroom Shine?</h2>
          <p className="text-zinc-400 text-lg mb-8">Book your detail in minutes. We'll come to you and deliver results that turn heads.</p>
          <button
            onClick={goToBooking}
            data-testid="cta-book-now-btn"
            className="bg-white text-black px-10 py-5 rounded-md text-lg font-semibold hover:bg-zinc-200 transition-all inline-flex items-center space-x-2"
          >
            <Calendar className="w-5 h-5" />
            <span>Book Your Detail Now</span>
          </button>
        </div>
      </section>

      {/* Contact/Footer Section */}
      <footer id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-xl font-bold mb-4">Mad Max Wax</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">Premium mobile detailing services in Central Columbus, OH. We bring the detail shop to you.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Contact</h3>
              <div className="space-y-3">
                <a href="tel:6149740991" data-testid="phone-link" className="flex items-center space-x-3 text-zinc-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>(614) 974-0991</span>
                </a>
                <a href="mailto:mmwllc1@gmail.com" data-testid="email-link" className="flex items-center space-x-3 text-zinc-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                  <span>mmwllc1@gmail.com</span>
                </a>
                <div className="flex items-center space-x-3 text-zinc-400">
                  <MapPin className="w-5 h-5" />
                  <span>Central Columbus, OH</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/madmaxwaxllc?igsh=MWYxcGFlZm1lcDJh"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-link"
                  aria-label="Follow Mad Max Wax on Instagram"
                  className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center hover:bg-zinc-800 transition-colors"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href="https://www.tiktok.com/@madmaxwaxllc?_r=1&_t=ZP-96t95V8g5GQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tiktok-link"
                  aria-label="Follow Mad Max Wax on TikTok"
                  className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center hover:bg-zinc-800 transition-colors"
                >
                  <Video className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-900 text-center text-zinc-500 text-sm">
            <p>© 2025 Mad Max Wax LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
